import { format } from "date-fns";
import { useSetAtom, useAtomValue } from "jotai";
import React from "react";

import {
  Box,
  Text,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";

import {
  getCalendarSettingsForDateAtom,
  getHoveredDateAtom,
  hoveredDateAtom,
} from "../atoms";
import { getBackroundRangeColoring } from "../hooks/getBackgroundRangeColoring";

type WeekDayProps = {
  position: number;
  monthDateObj: Date;
  curDateObj: Date;
};

export const WeekDay = ({
  curDateObj,
  monthDateObj,
  position,
}: WeekDayProps) => {
  const getCalendarSettingsForDate = useAtomValue(
    getCalendarSettingsForDateAtom
  );
  const setHoveredDate = useSetAtom(hoveredDateAtom);
  const hoveredDate = useAtomValue(getHoveredDateAtom);

  const {
    showDate,
    displayedDate,
    status,
    onDatePress,
    isSelected,
    selectedDates,
    discreteMultiSelect,
  } = getCalendarSettingsForDate(curDateObj, monthDateObj);

  const inRangeBackgroundColor = "surface-secondary";

  const { before, after, colors } = getBackroundRangeColoring(
    selectedDates,
    hoveredDate,
    displayedDate,
    status,
    discreteMultiSelect
  );

  return showDate ? (
    <>
      {position > 0 ? (
        <Box
          flex={1}
          backgroundColor={before ? inRangeBackgroundColor : undefined}
          height={40}
        />
      ) : null}
      <Box width={40} height={40} position={"relative"}>
        <Box
          position={"absolute"}
          top={0}
          left={0}
          zIndex={"back"}
          width={20}
          height={40}
          backgroundColor={before ? inRangeBackgroundColor : undefined}
        />
        <Pressable
          position={"relative"}
          zIndex={10}
          onHoverIn={() => setHoveredDate(displayedDate)}
          onHoverOut={() => {
            setHoveredDate(null);
          }}
          onPress={() => {
            onDatePress(displayedDate);
          }}
          disabled={status === "Disabled"}
          testID={`calendar-day-${displayedDate}`}
          accessibilityLabel={`${displayedDate} ${status}${
            isSelected ? " selected" : ""
          }`}
          backgroundColor={colors.neutral.surface}
          borderColor={isSelected ? "outline-primary" : colors.neutral.outline}
          _hover={{
            backgroundColor: colors.hovering.surface,
            borderColor: colors.hovering.outline,
          }}
          _pressed={{
            backgroundColor: colors.pressing.surface,
            borderColor: colors.pressing.outline,
          }}
          width={40}
          height={40}
          borderRadius={"1"}
          borderWidth={1.5}
          alignItems="center"
          justifyContent="center"
        >
          <Text
            color={status === "Disabled" ? "text-disabled" : "text-primary"}
            variant="mainBaseMedium"
            maxFontSizeMultiplier={1}
            textAlign={"center"}
          >
            {format(curDateObj, "d")}
          </Text>
        </Pressable>
        <Box
          position={"absolute"}
          top={0}
          left={20}
          zIndex={"back"}
          width={20}
          height={40}
          backgroundColor={after ? inRangeBackgroundColor : undefined}
        />
      </Box>
      {position < 6 ? (
        <Box
          flex={1}
          backgroundColor={after ? inRangeBackgroundColor : undefined}
          height={40}
        />
      ) : null}
    </>
  ) : (
    <>
      {position > 0 ? <Box flex={1} height={40} /> : null}
      <Box
        width={40}
        height={40}
        bg="transparent"
        testID={`placeholder-day-${displayedDate}`}
      />
      {position < 6 ? <Box flex={1} height={40} /> : null}
    </>
  );
};
